import * as React from 'react';

import { useInViewWithDwell } from '@notino/react-web-utils';
import type { AdProvider, TSponsoredAdsEvents } from '@notino/web-tracking';

import { MIN_SPONSORED_PRODUCTS } from '../SponsoredAdsProducts/constants';

import {
  getPlacementPayloads,
  getSponsoredPlacementInViewOptions,
  partitionOccurrencesByRender,
  sendSponsoredAdsReport,
  type SponsoredAdsOccurrence,
} from './tracking';

type Carousel = Parameters<typeof getPlacementPayloads>[0];

interface UseSponsoredAdsTrackingParams {
  carousel: Carousel;
  provider: AdProvider;
  occurrences: SponsoredAdsOccurrence[];
  cappedOccurrences: SponsoredAdsOccurrence[];
}

/**
 * Owns every sponsored-ads tracking side effect for the product-detail placement:
 * the placement `onLoad`/`onView` beacons and the per-product click/impression/
 * basket/wishlist handlers wired into ProductsSection.
 */
export const useSponsoredAdsTracking = ({
  carousel,
  provider,
  occurrences,
  cappedOccurrences,
}: UseSponsoredAdsTrackingParams) => {
  // First-occurrence wins: a duplicated product can only be disambiguated by slot
  // at click/view time, which ProductsSection does not expose (productId only).
  const trackingByCatalogId = React.useMemo(() => {
    const map = new Map<string, SponsoredAdsOccurrence>();
    for (const occurrence of cappedOccurrences) {
      if (!map.has(occurrence.productId)) {
        map.set(occurrence.productId, occurrence);
      }
    }
    return map;
  }, [cappedOccurrences]);

  // ProductsSection reports the web `id`, but occurrences are keyed by Criteo's
  // catalog id. Bridge web id -> catalog id from the resolved `vpProduct` (which
  // exposes both), now returned inline by the ProductDetailAds query.
  const catalogIdByWebId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const { vpProduct } of cappedOccurrences) {
      if (vpProduct && vpProduct.id !== null && vpProduct.catalogId !== null) {
        map.set(String(vpProduct.id), String(vpProduct.catalogId));
      }
    }
    return map;
  }, [cappedOccurrences]);

  const getTracking = React.useCallback(
    (webId: string) =>
      trackingByCatalogId.get(catalogIdByWebId.get(webId) ?? webId),
    [trackingByCatalogId, catalogIdByWebId]
  );

  // Placement onLoad — fired once, reporting which slots actually rendered and which
  // ad-provided products could not be loaded (`vpProduct` came back null) as
  // `unrenderedProducts`. Resolution is part of the ProductDetailAds query, so the
  // occurrences already carry it — no extra product request is needed.
  const loadReportedRef = React.useRef(false);
  React.useEffect(() => {
    if (
      loadReportedRef.current ||
      cappedOccurrences.length < MIN_SPONSORED_PRODUCTS ||
      !carousel
    ) {
      return;
    }

    const { renderedProducts, unrenderedProducts } =
      partitionOccurrencesByRender(cappedOccurrences);

    if (!renderedProducts.length) {
      return;
    }
    loadReportedRef.current = true;

    sendSponsoredAdsReport({
      requests: [
        {
          type: 'PlacementOnLoad',
          provider,
          payload: getPlacementPayloads(carousel)?.onLoadPayload ?? '',
          renderedProducts,
          unrenderedProducts,
        },
      ],
    });
  }, [cappedOccurrences, carousel, provider]);

  // Placement onView — dwell-validated visibility (Criteo: ≥50% for 1000ms).
  const { ref: viewRef, inView: placementInView } = useInViewWithDwell({
    ...getSponsoredPlacementInViewOptions(provider),
    skip: !occurrences.length,
  });
  const viewReportedRef = React.useRef(false);
  React.useEffect(() => {
    if (!placementInView || viewReportedRef.current || !carousel) {
      return;
    }
    const onViewPayload = getPlacementPayloads(carousel)?.onViewPayload;
    if (!onViewPayload) {
      return;
    }
    viewReportedRef.current = true;
    sendSponsoredAdsReport({
      requests: [{ type: 'PlacementOnView', provider, payload: onViewPayload }],
    });
  }, [placementInView, carousel, provider]);

  // Product-level events: resolve the occurrence for the reported web id, let the
  // caller build the event from its payloads, and beacon it if there's anything to send.
  const report = React.useCallback(
    (
      webId: string,
      build: (
        tracking: SponsoredAdsOccurrence
      ) => TSponsoredAdsEvents | undefined
    ) => {
      const tracking = getTracking(webId);
      const event = tracking && build(tracking);
      if (event) {
        sendSponsoredAdsReport({ requests: [event] });
      }
    },
    [getTracking]
  );

  const handlers = React.useMemo(
    () => ({
      onProductClick: (productId: string) =>
        report(productId, (t) =>
          t.onClickPayload
            ? {
                type: 'ProductOnClick',
                provider,
                payload: t.onClickPayload,
                slotId: t.slotId,
              }
            : undefined
        ),
      onProductImpression: (productId: string) =>
        report(productId, (t) =>
          t.onViewPayload
            ? { type: 'ProductOnView', provider, payload: t.onViewPayload }
            : undefined
        ),
      onProductBasketChange: (productId: string, quantity: number) =>
        report(productId, (t) =>
          t.onBasketChangePayload
            ? {
                type: 'ProductOnBasketChange',
                provider,
                payload: t.onBasketChangePayload,
                quantity,
              }
            : undefined
        ),
      onProductWishlist: (productId: string, wishlisted: boolean) =>
        report(productId, (t) =>
          // Contract only defines "added to wishlist"; ignore removals.
          wishlisted && t.onWishlistPayload
            ? {
                type: 'ProductOnWishlist',
                provider,
                payload: t.onWishlistPayload,
              }
            : undefined
        ),
    }),
    [report, provider]
  );

  return { viewRef, handlers };
};
